import React, {useState, useEffect} from "react";
import {useAuth} from "../../context/AuthContext";
import {getAppointmentsForService, cancelAppointment} from "../../services/appointments";
import Table from "../../components/Table/Table";
import ConfirmationModal from "../../components/Modals/ConfirmationModal/ConfirmationModal";
import {
    Alert,
    Snackbar,
} from "@mui/material";
import {useParams} from "react-router-dom";
import "../../theme/Noto";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicDropdown from "../../components/Selector/Selector";
import CustomDatePicker from "../../components/DatePicker/DatePicker";

export default function ManageAppointments() {
    const {serviceId} = useParams();
    const {token} = useAuth();

    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [appointmentToCancel, setAppointmentToCancel] = useState(null);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({
        message: "",
        type: ""
    });

    const statusMap = {
        pending: "قيد الانتظار",
        completed: "مكتمل",
        canceled: "ملغي"
    };

    const fetchAppointments = async () => {
        setIsLoading(true);
        try {
            const res = await getAppointmentsForService(token, serviceId, 42, startDate, endDate);
            let formatted = res.data.map(a => ({
                ...a,
                date: a.appointmentDate,
                time: `${a.startTime} - ${a.endTime}`,
                serviceName: a.appointmentSchedule?.service?.name || "",
                branchName: a.appointmentSchedule?.branch?.name || "",
                customerName: a.Customer?.name || "",
                dayName: a.dayName || "",
                status: statusMap[a.status] || a.status
            }));

            if (statusFilter) {
                formatted = formatted.filter(a => a.status === statusFilter);
            }

            setAppointments(formatted);
        } catch (err) {
            console.error(err);
            setSnackbarContent({message: "فشل في جلب المواعيد", type: "error"});
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const onCancel = async () => {
        setIsLoading(true);
        setConfirmationModalOpen(false);
        try {
            await cancelAppointment(token, appointmentToCancel);
            setAppointments(prev =>
                prev.map(a =>
                    a.id === appointmentToCancel ? {...a, status: "ملغي"} : a
                )
            );
            setSnackbarContent({message: "تم إلغاء الموعد بنجاح", type: "success"});
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarContent({message: err.response?.data || err.message, type: "error"});
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, [startDate, endDate, statusFilter]);

    const exportToPDF = () => {
        const doc = new jsPDF();

        doc.setFont('Noto');
        doc.setFontSize(12);
        doc.text("قائمة المواعيد", 105, 15, {align: "center"});

        autoTable(doc, {
            head: [[
                'التاريخ', 'اليوم', 'الوقت',
                'اسم العميل', 'الفرع', 'الخدمة', 'الحالة'
            ]],
            body: appointments.map(a => [
                a.date,
                a.dayName || "",
                a.time,
                a.customerName,
                a.branchName,
                a.serviceName,
                a.status
            ]),
            styles: {
                font: 'Noto',
                fontStyle: 'normal',
                fontSize: 10,
                halign: 'center',
                cellPadding: {top: 6, bottom: 6}, // ✅ Increase row height
            },
            headStyles: {
                fillColor: [22, 160, 133],
                halign: 'right',
                cellPadding: {top: 8, bottom: 8} // optional, to make header slightly taller
            },
            startY: 25
        });

        doc.save("appointments.pdf");
    };

    const columns = [
        {key: "date", label: "تاريخ الموعد"},
        {key: "dayName", label: "اليوم"},
        {key: "time", label: "الوقت"},
        {key: "customerName", label: "اسم العميل"},
        {key: "branchName", label: "الفرع"},
        {key: "serviceName", label: "الخدمة"},
        {key: "status", label: "الحالة"}
    ];

    return (
        <div className="page">
            <div className='content-view'>

                <div className="mb-6">
                    <h1 className="text-xl font-bold mb-4">إدارة المواعيد</h1>
                    <div style={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: 'center',
                        gap: "1rem",
                        marginBottom: "1rem",
                        flexWrap: "wrap"
                    }}>
                        <CustomDatePicker
                            label="من"
                            value={startDate}
                            onChange={setStartDate}
                        />

                        <CustomDatePicker
                            label="إلى"
                            value={endDate}
                            onChange={setEndDate}
                        />

                        <DynamicDropdown
                            label="الحالة"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                {value: "", label: "الكل"},
                                {value: "قيد الانتظار", label: "قيد الانتظار"},
                                {value: "مكتمل", label: "مكتمل"},
                                {value: "ملغي", label: "ملغي"},
                            ]}
                        />

                        <button
                            onClick={exportToPDF}
                            className='btn filled'>
                            تصدير إلى PDF
                        </button>
                    </div>
                </div>

                <Table
                    onDelete={(id) => {
                        setAppointmentToCancel(id);
                        setConfirmationModalOpen(true);
                    }}
                    loading={isLoading}
                    editableColumns={[]}
                    highlightConfig={{
                        status: {
                            "قيد الانتظار": "#facc15",
                            "مكتمل": "#01c8aa",
                            "ملغي": "#ef4444"
                        }
                    }}
                    data={appointments}
                    columns={columns}
                    tagColumn="status"
                />

                {confirmationModalOpen && (
                    <ConfirmationModal
                        message="هل أنت متأكد أنك تريد إلغاء هذا الموعد؟"
                        handleConfirm={onCancel}
                        onClose={() => setConfirmationModalOpen(false)}
                    />
                )}

                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarContent.type}
                        variant="filled"
                        sx={{width: '100%'}}
                    >
                        {snackbarContent.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
